/**
 * NewPassword page for Wadakaroo
 *
 * The destination of the reset-link email (`/new-password?token=...`). The user
 * sets a new password using the single-use token from the link — no login
 * required. On success they're sent to the sign-in page.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { resetPasswordWithTokenApi } from '../services/api';

// Mirror the backend PasswordPolicy so the user gets instant feedback.
const PASSWORD_RULES = [
    { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { id: 'digit', label: 'One number', test: (p) => /\d/.test(p) },
];

const NewPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);

    const ruleResults = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(newPassword) }));
    const allRulesPass = ruleResults.every((r) => r.ok);
    const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
    const canSubmit = !!token && allRulesPass && passwordsMatch && !isLoading;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setIsLoading(true);
        setError(null);
        try {
            await resetPasswordWithTokenApi({ token, newPassword });
            setDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not reset your password. The link may have expired.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-10 justify-center">
                    <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-display font-bold text-lg">W</span>
                    </div>
                    <span className="text-gray-900 font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                    {done ? (
                        <div className="text-center">
                            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="text-emerald-600" size={24} />
                            </div>
                            <h2 className="text-xl font-display font-bold text-gray-900 mb-2">Password reset</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Your password has been updated. Redirecting you to sign in…
                            </p>
                            <Link to="/login" className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
                                Go to sign in
                            </Link>
                        </div>
                    ) : !token ? (
                        <div className="text-center">
                            <h2 className="text-xl font-display font-bold text-gray-900 mb-2">Invalid reset link</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                This password reset link is missing or malformed. Please request a new one.
                            </p>
                            <Link
                                to="/forgot-password"
                                className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700"
                            >
                                Request a new link
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">Set a new password</h2>
                                <p className="text-gray-500 text-sm">Choose a strong password for your account.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                        <p className="text-red-700 text-sm">{error}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full pl-11 pr-11 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                            placeholder="••••••••"
                                            required
                                            autoComplete="new-password"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((s) => !s)}
                                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                            placeholder="••••••••"
                                            required
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {confirmPassword.length > 0 && !passwordsMatch && (
                                        <p className="mt-1.5 text-xs text-red-600">Passwords do not match.</p>
                                    )}
                                </div>

                                <ul className="space-y-1.5">
                                    {ruleResults.map((r) => (
                                        <li key={r.id} className="flex items-center gap-2 text-xs">
                                            <CheckCircle2
                                                size={14}
                                                className={r.ok ? 'text-emerald-600' : 'text-gray-300'}
                                            />
                                            <span className={r.ok ? 'text-gray-700' : 'text-gray-400'}>{r.label}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                                    Reset password
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                                >
                                    <ArrowLeft size={16} /> Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewPassword;
